import { useState } from "react";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { reais, textoParaCentavos, type ConfigFinanceira } from "@/lib/dinheiro";
import { cn } from "@/lib/cn";
import { campo, moldura, rotulo } from "../estilos";

const pct = (bps: number | null) => (bps == null ? "" : (bps / 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 }));
function paraBps(t: string): number | null {
  const limpo = t.replace("%", "").trim();
  if (!limpo) return null;
  const n = Number(limpo.includes(",") ? limpo.replace(/\./g, "").replace(",", ".") : limpo);
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
}

function Formulario({ config, pode }: { config: ConfigFinanceira; pode: boolean }) {
  const utils = trpc.useUtils();
  const [cartao, setCartao] = useState(pct(config.taxaDoCartaoBps));
  const [boleto, setBoleto] = useState(config.tarifaDoBoletoCents ? reais(config.tarifaDoBoletoCents) : "");
  const bps = paraBps(cartao);
  const tarifa = boleto.trim() ? textoParaCentavos(boleto) || 0 : null;
  const invalida = bps != null && (Number.isNaN(bps) || bps < 0 || bps > 2000);
  const mudou = bps !== config.taxaDoCartaoBps || tarifa !== config.tarifaDoBoletoCents;
  const salvar = trpc.financeiro.configFinanceira.salvar.useMutation({
    onSuccess: () => {
      toast.success("Taxas salvas. Valem para os próximos recebimentos.");
      utils.financeiro.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  return (
    <div className="grid gap-3 px-4 pb-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
      <label className="block">
        <span className={rotulo}>Taxa do cartão (%)</span>
        <input value={cartao} disabled={!pode} inputMode="decimal" placeholder="Ex.: 3,5" onChange={(e) => setCartao(e.target.value)} className={cn(campo, invalida && "border-red-400")} />
      </label>
      <label className="block">
        <span className={rotulo}>Tarifa do boleto (R$ por boleto)</span>
        <input value={boleto} disabled={!pode} inputMode="decimal" placeholder="Ex.: 2,50" onChange={(e) => setBoleto(e.target.value)} className={campo} />
      </label>
      {pode && (
        <button
          type="button"
          disabled={!mudou || invalida || salvar.isPending}
          onClick={() =>
            salvar.mutate({
              dreLigado: config.dreLigado,
              regime: config.regime,
              aliquotaSobreFaturamentoBps: config.aliquotaSobreFaturamentoBps,
              custoDoMaterial: config.custoDoMaterial,
              taxaDoCartaoBps: bps,
              tarifaDoBoletoCents: tarifa,
            })
          }
          className="h-9 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
        >
          Salvar
        </button>
      )}
      {invalida && <p className="text-[11px] text-red-600 sm:col-span-3">Informe uma taxa de 0 a 20%.</p>}
    </div>
  );
}

/** Taxa do cartão e tarifa do boleto: lançadas sozinhas a cada recebimento (resultado financeiro). */
export function TaxasDeRecebimento() {
  const pode = usePermissao("finance.manage");
  const config = trpc.financeiro.configFinanceira.ler.useQuery().data;
  if (!config) return null;
  return (
    <section className={cn(moldura, "overflow-hidden")}>
      <div className="flex items-start gap-3 px-4 py-4">
        <CreditCard className="mt-0.5 h-5 w-5 shrink-0 text-brand-600 dark:text-primary" />
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Taxas de recebimento</h3>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-muted-foreground">
            Ao registrar um recebimento no cartão ou no boleto, a taxa entra sozinha como saída paga no mesmo dia, em "Taxas de cartão e boleto". Estornar o recebimento estorna a taxa junto.
          </p>
        </div>
      </div>
      <Formulario key={`${config.taxaDoCartaoBps}-${config.tarifaDoBoletoCents}`} config={config} pode={pode} />
    </section>
  );
}
